import { redirect } from 'react-router-dom'

import { ActivityTokenType } from '@cash-web/protos/squareup/cash/activity/api/v1/coremodels.pb'
import { JourneyRoute, LoaderFunctionArgsWithContext } from '@cash-web/shared-journey-util-main'

export const activityConfirmationFlowRoute = {
  id: 'journey-activity-confirmation-flow',
  trackingName: 'Journey Activity Confirmation Flow',
  path: 'web/view/activity/confirmation/:transaction_id',
  lazy: async () => ({
    Component: (await import('@cash-web/account-activity-feature-main')).JourneyActivityConfirmationFlow,
  }),
} as const

export const activityTokenCategoryRoute = {
  id: 'journey-activity-token-category',
  trackingName: 'Journey Activity Category',
  path: 'web/view/activity/:activityType',
  lazy: async () => ({
    Component: (await import('@cash-web/account-activity-feature-main')).JourneyActivityTokenSubCategory,
  }),
} as const

export const activityTokenSubCategoryRoute = {
  id: 'journey-activity-token-sub-category',
  trackingName: 'Journey Activity Sub Category',
  path: 'web/view/activity/:activityType/:subToken',
  lazy: async () => ({
    Component: (await import('@cash-web/account-activity-feature-main')).JourneyActivityTokenSubCategory,
  }),
} as const

export const activityItemRoute = {
  id: 'journey-activity-item',
  trackingName: 'Journey Activity Item',
  path: 'web/view/activity/item/:activityType/:itemToken',
  lazy: async () => ({
    Component: (await import('@cash-web/account-activity-feature-main')).JourneyActivityItem,
  }),
  modalVariant: 'receipt',
} as const

export const activityNotSupportedRoute = {
  id: 'journey-activity-not-supported',
  trackingName: 'Journey Activity Not Supported',
  path: 'web/view/activity/not-supported',
  lazy: async () => ({
    Component: (await import('@cash-web/account-activity-feature-main')).JourneyActivityUnsupportedOpenMobileApp,
  }),
} as const

export const activityReceiptV2Route = {
  id: 'journey-activity-receipt-v2',
  trackingName: 'Journey Activity Receipt V2',
  path: 'dl/view/activity/receipt-v2/:encodedProto',
  lazy: async () => ({
    Component: (await import('@cash-web/account-activity-feature-main')).JourneyActivityReceiptV2,
  }),
  modalVariant: 'receipt',
  target: 'modal',
} as const

export const activityViewPaymentDetailsRoute = {
  id: 'journey-activity-view-payment-details',
  trackingName: 'Journey Activity View Payment Details',
  path: 'dl/view/activity/payment/:paymentToken',
  loader: async ({ params, context, request }) => {
    const url = new URL(request.url)
    const queryString = url.search
    const paymentToken = params['paymentToken']
    if (!paymentToken) {
      throw new Error('JourneyActivityViewPaymentDetails: Missing payment token')
    }

    context?.browserNavigate(`/account/activity/${paymentToken}${queryString}`)
  },
} as const satisfies JourneyRoute

export const activityViewPaymentDetailsReceiptRoute = {
  id: 'journey-activity-view-payment-details-receipt',
  trackingName: 'Journey Activity View Payment Details Receipt',
  path: 'dl/view/activity/payment/:paymentToken/receipt/details',
  loader: async ({ params, context, request }) => {
    const url = new URL(request.url)
    const queryString = url.search
    const paymentToken = params['paymentToken']
    if (!paymentToken) {
      throw new Error('JourneyActivityViewPaymentDetailsReceipt: Missing payment token')
    }

    context?.browserNavigate(`/account/activity/${paymentToken}${queryString}`)
  },
} as const satisfies JourneyRoute

export const treehouseAppLinkActivityReceiptRoute = {
  id: 'treehouse-activity-receipt',
  trackingName: 'Treehouse Activity Receipt',
  // eslint-disable-next-line @nx/workspace/enforce-client-routes -- https://clientroutes.cash.app/client_routes/treehouse_app_link
  path: 'treehouse/activity/receipt/:itemToken',
  modalVariant: 'receipt',
  loader: async ({ params, request }) => {
    const url = new URL(request.url)
    const itemToken = params['itemToken']
    if (!itemToken) {
      throw new Error('TreehouseActivityReceipt: Missing item token')
    }

    throw redirect(`/web/view/activity/item/${ActivityTokenType.CUSTOMER_TOKEN}/${itemToken}${url.search}`)
  },
} as const satisfies JourneyRoute

export const activityViewHomeRoute = {
  id: 'activity-view-home',
  trackingName: 'Activity View Home',
  path: 'dl/view/activity',
  loader: async ({ context }: LoaderFunctionArgsWithContext) => {
    context?.browserNavigate('/account/activity')
  },
} as const

export const activityClientRoutes = [
  activityConfirmationFlowRoute,
  activityTokenCategoryRoute,
  activityTokenSubCategoryRoute,
  activityItemRoute,
  activityNotSupportedRoute,
  activityReceiptV2Route,
  activityViewHomeRoute,
  activityViewPaymentDetailsRoute,
  activityViewPaymentDetailsReceiptRoute,
  treehouseAppLinkActivityReceiptRoute,
] as const satisfies JourneyRoute[]
